import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  FaSave, FaArrowLeft, FaPlus, FaTrash, FaUpload, FaStar, FaInfoCircle
} from 'react-icons/fa';
import { adminApi } from '../../services/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

const TYPES = [
  { value: 'CASA', label: 'Casa' },
  { value: 'APARTAMENTO', label: 'Apartamento' },
  { value: 'TERRENO', label: 'Terreno' },
  { value: 'COMERCIAL', label: 'Comercial' },
  { value: 'OFICINA', label: 'Oficina' },
];

const STATUSES = [
  { value: 'DISPONIBLE', label: 'Disponible' },
  { value: 'VENDIDA', label: 'Vendida' },
  { value: 'ALQUILADA', label: 'Alquilada' },
  { value: 'RESERVADA', label: 'Reservada' },
];

const AdminPropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState([]);
  const [featureInput, setFeatureInput] = useState('');
  const [images, setImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [videos, setVideos] = useState([]);
  const [uploadingVid, setUploadingVid] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      currency: 'USD',
      type: 'CASA',
      status: 'DISPONIBLE',
      country: 'El Salvador',
      featured: false,
    }
  });

  const loadProperty = useCallback(async () => {
    try {
      const res = await adminApi.getAllProperties();
      const props = res.data?.data || [];
      const prop = props.find(p => p.id === parseInt(id));
      if (!prop) { toast.error('Propiedad no encontrada'); navigate('/admin12/propiedades'); return; }
      reset({
        title: prop.title,
        description: prop.description,
        price: prop.price,
        currency: prop.currency,
        type: prop.type,
        status: prop.status,
        bedrooms: prop.bedrooms,
        bathrooms: prop.bathrooms,
        area: prop.area,
        address: prop.address,
        city: prop.city,
        country: prop.country,
        latitude: prop.latitude,
        longitude: prop.longitude,
        featured: prop.featured,
      });
      setFeatures(prop.features || []);
      setImages(prop.imageUrls || []);
      setVideos(prop.videoUrls || []);
    } catch (_) {
      toast.error('Error al cargar la propiedad');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, reset]);

  useEffect(() => {
    if (isEdit) loadProperty();
  }, [isEdit, loadProperty]);

  const addFeature = () => {
    const val = featureInput.trim();
    if (val && !features.includes(val)) {
      setFeatures(prev => [...prev, val]);
      setFeatureInput('');
    }
  };

  const removeFeature = (f) => setFeatures(prev => prev.filter(x => x !== f));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (!id) { toast.error('Guarda la propiedad primero antes de subir imágenes'); return; }
    setUploadingImg(true);
    let lastUrls = images;
    let errors = 0;
    for (const file of files) {
      try {
        const res = await adminApi.uploadImage(id, file);
        lastUrls = res.data?.data?.imageUrls || lastUrls;
      } catch (err) {
        errors++;
        toast.error(`Error al subir "${file.name}": ${err.response?.data?.message || err.message}`);
      }
    }
    setImages(lastUrls);
    const ok = files.length - errors;
    if (ok > 0) toast.success(`${ok} imagen${ok > 1 ? 'es' : ''} subida${ok > 1 ? 's' : ''} correctamente`);
    setUploadingImg(false);
    e.target.value = '';
  };

  const handleRemoveImage = async (url) => {
    if (!id) return;
    try {
      const res = await adminApi.removeImage(id, url);
      setImages(res.data?.data?.imageUrls || images.filter(i => i !== url));
      toast.success('Imagen eliminada');
    } catch (_) {
      toast.error('Error al eliminar imagen');
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (!id) { toast.error('Guarda la propiedad primero antes de subir videos'); return; }
    setUploadingVid(true);
    let lastUrls = videos;
    let errors = 0;
    for (const file of files) {
      try {
        const res = await adminApi.uploadVideo(id, file);
        lastUrls = res.data?.data?.videoUrls || lastUrls;
      } catch (err) {
        errors++;
        toast.error(`Error al subir "${file.name}": ${err.response?.data?.message || err.message}`);
      }
    }
    setVideos(lastUrls);
    const ok = files.length - errors;
    if (ok > 0) toast.success(`${ok} video${ok > 1 ? 's' : ''} subido${ok > 1 ? 's' : ''} correctamente`);
    setUploadingVid(false);
    e.target.value = '';
  };

  const handleRemoveVideo = async (url) => {
    if (!id) return;
    try {
      const res = await adminApi.removeVideo(id, url);
      setVideos(res.data?.data?.videoUrls || videos.filter(v => v !== url));
      toast.success('Video eliminado');
    } catch (_) {
      toast.error('Error al eliminar video');
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        price: parseFloat(data.price),
        bedrooms: data.bedrooms ? parseInt(data.bedrooms) : undefined,
        bathrooms: data.bathrooms ? parseInt(data.bathrooms) : undefined,
        area: data.area ? parseFloat(data.area) : undefined,
        latitude: data.latitude ? parseFloat(data.latitude) : undefined,
        longitude: data.longitude ? parseFloat(data.longitude) : undefined,
        features,
        featured: Boolean(data.featured),
      };

      if (isEdit) {
        await adminApi.updateProperty(id, payload);
        toast.success('Propiedad actualizada');
      } else {
        const res = await adminApi.createProperty(payload);
        const newId = res.data?.data?.id;
        toast.success('Propiedad creada');
        navigate(`/admin12/propiedades/editar/${newId}`);
        return;
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al guardar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <AdminLayout title={isEdit ? 'Editar Propiedad' : 'Nueva Propiedad'}>
      <div className="admin-loading"><div className="spinner" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title={isEdit ? 'Editar Propiedad' : 'Nueva Propiedad'}>
      <div className="form-toolbar">
        <button className="btn btn--outline-navy" onClick={() => navigate('/admin12/propiedades')}>
          <FaArrowLeft /> Volver
        </button>
        <button
          className="btn btn--gold"
          onClick={handleSubmit(onSubmit)}
          disabled={saving}
        >
          <FaSave /> {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-sections">
          {/* Info básica */}
          <div className="admin-card">
            <h3 className="form-section-title">Información básica</h3>
            <div className="form-group">
              <label>Título *</label>
              <input
                {...register('title', { required: 'El título es requerido' })}
                placeholder="Ej: Casa Moderna en Santa Elena"
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="form-error">{errors.title.message}</span>}
            </div>

            <div className="form-group">
              <label>Descripción *</label>
              <textarea
                {...register('description', { required: 'La descripción es requerida' })}
                rows={4}
                placeholder="Describe la propiedad..."
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <span className="form-error">{errors.description.message}</span>}
            </div>

            <div className="form-row form-row--3">
              <div className="form-group">
                <label>Tipo</label>
                <select {...register('type')}>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Estado</label>
                <select {...register('status')}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Destacada</label>
                <div className="checkbox-field">
                  <input type="checkbox" id="featured" {...register('featured')} />
                  <label htmlFor="featured" className="checkbox-label">
                    <FaStar /> Destacar propiedad
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Precio */}
          <div className="admin-card">
            <h3 className="form-section-title">Precio</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Precio *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('price', {
                    required: 'El precio es requerido',
                    min: { value: 0, message: 'Debe ser positivo' }
                  })}
                  placeholder="150000"
                  className={errors.price ? 'error' : ''}
                />
                {errors.price && <span className="form-error">{errors.price.message}</span>}
              </div>
              <div className="form-group">
                <label>Moneda</label>
                <select {...register('currency')}>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Características */}
          <div className="admin-card">
            <h3 className="form-section-title">Características</h3>
            <div className="form-row form-row--3">
              <div className="form-group">
                <label>Habitaciones</label>
                <input type="number" min="0" {...register('bedrooms')} placeholder="3" />
              </div>
              <div className="form-group">
                <label>Baños</label>
                <input type="number" min="0" {...register('bathrooms')} placeholder="2" />
              </div>
              <div className="form-group">
                <label>Área (m²)</label>
                <input type="number" min="0" step="0.01" {...register('area')} placeholder="200" />
              </div>
            </div>

            {/* Features dinámicas */}
            <div className="form-group">
              <label>Características adicionales</label>
              <div className="feature-input">
                <input
                  value={featureInput}
                  onChange={e => setFeatureInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  placeholder="Ej: Piscina, Jardín, Garaje..."
                />
                <button type="button" className="btn btn--navy" onClick={addFeature}>
                  <FaPlus />
                </button>
              </div>
              <div className="feature-tags">
                {features.map(f => (
                  <span key={f} className="feature-tag">
                    {f}
                    <button type="button" onClick={() => removeFeature(f)}><FaTrash /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="admin-card">
            <h3 className="form-section-title">Ubicación</h3>
            <div className="form-group">
              <label>Dirección *</label>
              <input
                {...register('address', { required: 'La dirección es requerida' })}
                placeholder="Col. Escalón, Calle El Pedregal #45"
                className={errors.address ? 'error' : ''}
              />
              {errors.address && <span className="form-error">{errors.address.message}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ciudad *</label>
                <input
                  {...register('city', { required: 'La ciudad es requerida' })}
                  placeholder="San Salvador"
                  className={errors.city ? 'error' : ''}
                />
                {errors.city && <span className="form-error">{errors.city.message}</span>}
              </div>
              <div className="form-group">
                <label>País</label>
                <input {...register('country')} placeholder="El Salvador" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Latitud (para mapa)</label>
                <input
                  type="number"
                  step="any"
                  {...register('latitude')}
                  placeholder="13.6929"
                />
                <small className="form-hint">Ej: 13.6929 (El Salvador)</small>
              </div>
              <div className="form-group">
                <label>Longitud (para mapa)</label>
                <input
                  type="number"
                  step="any"
                  {...register('longitude')}
                  placeholder="-89.2182"
                />
                <small className="form-hint">Ej: -89.2182 (El Salvador)</small>
              </div>
            </div>
          </div>

          {/* Imágenes */}
          {isEdit && (
            <div className="admin-card">
              <h3 className="form-section-title">Imágenes</h3>

              <div className="image-upload-area">
                <label className="upload-btn">
                  <FaUpload />
                  {uploadingImg ? 'Subiendo...' : 'Subir imágenes'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImg}
                    style={{ display: 'none' }}
                  />
                </label>
                <p>JPG, PNG, WebP, GIF — Máx 10MB — Puedes seleccionar varias a la vez</p>
              </div>

              <div className="image-grid">
                {images.map((url, i) => (
                  <div key={i} className="image-item">
                    <img src={url} alt={`Imagen ${i + 1}`} />
                    <button
                      type="button"
                      className="image-remove"
                      onClick={() => handleRemoveImage(url)}
                    >
                      <FaTrash />
                    </button>
                    {i === 0 && <span className="image-main-badge">Principal</span>}
                  </div>
                ))}
              </div>

              {images.length === 0 && (
                <p className="admin-empty">Sin imágenes. Sube la primera usando el botón de arriba.</p>
              )}
            </div>
          )}

          {/* Videos */}
          {isEdit && (
            <div className="admin-card">
              <h3 className="form-section-title">Videos</h3>

              <div className="image-upload-area">
                <label className="upload-btn">
                  <FaUpload />
                  {uploadingVid ? 'Subiendo...' : 'Subir videos'}
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleVideoUpload}
                    disabled={uploadingVid}
                    style={{ display: 'none' }}
                  />
                </label>
                <p>MP4, WebM, MOV, AVI — Máx 100MB — Puedes seleccionar varios a la vez</p>
              </div>

              <div className="image-grid">
                {videos.map((url, i) => (
                  <div key={i} className="image-item">
                    <div style={{ width: '100%', height: '100%', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', textAlign: 'center', padding: '8px' }}>
                      <span>Video {i + 1}</span>
                    </div>
                    <button
                      type="button"
                      className="image-remove"
                      onClick={() => handleRemoveVideo(url)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>

              {videos.length === 0 && (
                <p className="admin-empty">Sin videos. Sube el primero usando el botón de arriba.</p>
              )}
            </div>
          )}

          {!isEdit && (
            <div className="admin-card admin-card--info">
              <p><FaInfoCircle style={{ color: '#a07830', marginRight: '6px', verticalAlign: 'middle' }} /><strong>Nota:</strong> Guarda la propiedad primero. Luego podrás subir imágenes desde la pantalla de edición.</p>
            </div>
          )}
        </div>

        <div className="form-toolbar form-toolbar--bottom">
          <button className="btn btn--outline-navy" type="button" onClick={() => navigate('/admin12/propiedades')}>
            Cancelar
          </button>
          <button className="btn btn--gold btn--lg" type="submit" disabled={saving}>
            <FaSave /> {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear propiedad'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminPropertyForm;
